import { WuJieBus } from '@analytics/patsnap-copilot/src/wujie-bus';
import { AbstractAnalyticsCopilotServiceImpl } from '@analytics/shared/services/analytics-copilot-service';
import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { headerService } from './header-service';
import { ParagraphHighlightService } from './paragraph-highlight-service.interface';
import { PatentViewCopilotContext } from './patent-view-copilot-context';
import type { PatsnapCopilotService } from '@analytics/patsnap-copilot/src/patsnap-copilot-service.interface';
import { getI18nInstance } from '@/locales/i18nInstance';
import { CORE_SEARCH_SERVICE_URI } from 'library-@/config/apiConfig';
import { FactoryPsHttpSSE } from '@patsnap-utils/sse';

@injectable()
export class PatsnapCopilotServiceImpl extends AbstractAnalyticsCopilotServiceImpl implements PatsnapCopilotService {
    constructor(
        @inject(WuJieBus)
        private readonly _wujieBus: WuJieBus,
        @inject(PatentViewCopilotContext)
        private readonly _patentViewCopilotContext: PatentViewCopilotContext,
        @inject(ParagraphHighlightService)
        private readonly _paragraphHighlightService: ParagraphHighlightService,
        @inject(FactoryPsHttpSSE)
        private readonly _getFactoryPsHttpSSE: FactoryPsHttpSSE,
    ) {
        super();
        this._wujieBus.$on('copilot-loaded', (isLoaded: boolean) => {
            if (isLoaded) {
                this._wujieBus.$emit('copilot-ready', true);
                this._paragraphHighlightService.updateContextMenu();
            }
        });
    }

    historyIdentifier() {
        return headerService.account?._info.account_id || '';
    }

    pageParamFunc() {
        return this._patentViewCopilotContext.getContext();
    }

    source = 'ANALYTICS_VIEW';

    getSSEConfig() {
        return {
            path: `${CORE_SEARCH_SERVICE_URI}/core-search-api/search/sse/copilot/view`,
            from: this.source,
        };

    }

    getFactoryPsHttpSSE() {
        return this._getFactoryPsHttpSSE;
    }

    welcomeMessages() {
        const helloMsgs = [{
            title: {},
            prompt: {
                cn: '本专利技术效果是什么？',
                en: 'Technology effect of this patent',
                jp: 'この特許の技術効果は何ですか？',
                tw: '本專利技術效果是什麽？',
            },
            intent: 'summary_efficacy',
        }, {
            title: {},
            prompt: {
                cn: '这篇专利主要的发明点是什么？',
                en: 'Technology method of this patent',
                jp: 'この特許の主な発明ポイントは何ですか？',
                tw: '這篇專利主要的發明點是什麽？',
            },
            intent: 'summary_means',
        }, {
            title: {},
            prompt: {
                cn: '本专利解决了什么技术问题？',
                en: 'Technology problem to be resolved of this patent',
                jp: 'この特許はどの技術的な問題を解決していますか？',
                tw: '本專利解決了什麽技術問題？',
            },
            intent: 'summary_problem',
        }, {
            title: {},
            prompt: {
                cn: '总结一下实施例',
                en: 'Summarize the embodiments of this patent',
                jp: 'この特許の実施例を要約してください。',
                tw: '總結一下實施例',
            },
            intent: 'summary_embodiment',
        }, {
            title: {},
            prompt: {
                cn: '总结权利要求',
                en: 'Summarize the claims of this patent',
                jp: '特許請求項を要約してください。',
                tw: '總結權利要求',
            },
            intent: 'summary_claims',
        }, {
            title: {},
            prompt: {
                cn: '对比一下本专利的实施例',
                en: 'Compare the embodiments of this patent',
                jp: 'この特許の実施例を比較してください。',
                tw: '對比一下本專利的實施例',
            },
            intent: 'compare_embodiment',
        }];

        const lang = this.getCurrentLanguage();

        const suggestions = helloMsgs.map(({ title, prompt, intent }) => {
            return {
                title: title[lang],
                prompt: prompt[lang],
                intent: intent,
            };
        });

        return Promise.resolve(suggestions);
    }

    getSocketType() {
        return 'COPILOT_VIEW';
    }

    getAdvices() {
        const i18n = getI18nInstance();
        return [{
            title: i18n.t('copilotAdvices.title1'),
            desc: i18n.t('copilotAdvices.desc1'),
        }, {
            title: i18n.t('copilotAdvices.title2'),
            desc: i18n.t('copilotAdvices.desc2'),
        }, {
            title: i18n.t('copilotAdvices.title3'),
            desc: i18n.t('copilotAdvices.desc3'),
        }, {
            title: i18n.t('copilotAdvices.title4'),
            desc: i18n.t('copilotAdvices.desc4'),
        }, {
            title: i18n.t('copilotAdvices.title5'),
            desc: i18n.t('copilotAdvices.desc5'),
        }];
    }

    menuList() {
        return [{
            builtInContent: 'chat',
        }, {
            builtInContent: 'advice',
        }];
    }
}
